/**
 * Cookie / consent management — RGPD compliant.
 *
 * Wrapper autour de `window.Shopify.customerPrivacy` (Shopify Customer
 * Privacy API). Sans cette API → fallback "strict" (rien d'optionnel
 * autorisé) pour rester safe RGPD côté EU.
 *
 * Catégories :
 *   - **Strictly necessary** (toujours OK, exempté CNIL) :
 *     cart_id, locale, country, cart-countdown, last-quote (cookies
 *     essentiels au fonctionnement du e-commerce — exempté ePrivacy).
 *   - **Preferences** (consent "preferences" requis) :
 *     last-viewed et personnalisations passives non-essentielles.
 *     La wishlist guest est traitée comme stockage fonctionnel explicitement
 *     demandé par le clic utilisateur et reste disponible après refus cookies.
 *   - **Analytics** (consent "analytics" requis) :
 *     Shopify Analytics events (page_viewed, product_viewed, add_to_cart).
 *   - **Marketing** (consent "marketing" requis) :
 *     Shopify Marketing pixel, retargeting tags.
 *
 * Au boot : écoute `visitorConsentCollected` event Shopify et dispatch
 * `smootify:consent_changed` sur document → les modules consommateurs
 * (analytics, last-viewed, etc.) re-évaluent leurs autorisations.
 */

interface ShopifyCustomerPrivacy {
  userCanBeTracked?: () => boolean;
  marketingAllowed?: () => boolean;
  preferencesAllowed?: () => boolean;
  analyticsProcessingAllowed?: () => boolean;
  saleOfDataAllowed?: () => boolean;
  shouldShowBanner?: () => boolean;
  getRegulation?: () => string; // "GDPR" | "CCPA" | ""
  getRegion?: () => string;
  getTrackingConsent?: () => "yes" | "no" | "";
  setTrackingConsent?: (
    consent: { analytics?: boolean; marketing?: boolean; preferences?: boolean; sale_of_data?: boolean },
    onComplete?: () => void,
  ) => void;
}

interface ShopifyGlobal {
  customerPrivacy?: ShopifyCustomerPrivacy;
  loadFeatures?: (
    features: Array<{ name: string; version: string }>,
    cb?: (err?: Error) => void,
  ) => void;
}

declare global {
  interface Window {
    Shopify?: ShopifyGlobal;
  }
}

function cp(): ShopifyCustomerPrivacy | null {
  if (typeof window === "undefined") return null;
  return window.Shopify?.customerPrivacy ?? null;
}

// ---------------------------------------------------------------------------
//  Local fallback storage
// ---------------------------------------------------------------------------
// Sur les sites SANS Shopify.customerPrivacy (ex: staging Webflow pendant
// le dev, ou snippet Shopify analytics non chargé), on stocke le consent
// dans localStorage `lmdr_consent` pour persister la décision user.

const LOCAL_KEY = "lmdr_consent";

interface LocalConsent {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: number;
}

function readLocal(): LocalConsent | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalConsent;
    if (typeof parsed.analytics !== "boolean") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocal(c: LocalConsent): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(LOCAL_KEY, JSON.stringify(c));
  } catch {
    /* noop */
  }
}

/**
 * L'user est-il en zone RGPD/CCPA (consent requis) ?
 * Hors zone régulée → tracking par défaut OK sans bannière.
 */
export function isRegulatedRegion(): boolean {
  const api = cp();
  if (!api) return true; // Safe default : assume regulated
  const reg = api.getRegulation?.();
  return reg === "GDPR" || reg === "CCPA";
}

export function shouldShowBanner(): boolean {
  const api = cp();
  if (api) return api.shouldShowBanner?.() ?? false;
  // Fallback : pas d'API → afficher si pas de consent local + zone régulée
  return isRegulatedRegion() && !consentGiven();
}

/**
 * Le user a-t-il accepté le tracking ANALYTICS (mesure d'audience) ?
 * Concerne : Shopify Analytics events (page_viewed, add_to_cart…), GA4, etc.
 *
 * Hors zone régulée → true par défaut.
 */
export function analyticsAllowed(): boolean {
  const api = cp();
  if (api) {
    // Shopify a 2 méthodes : analyticsProcessingAllowed (récent) ou
    // userCanBeTracked (legacy). Préfère la 1ère, fallback la 2nde.
    const v = api.analyticsProcessingAllowed?.() ?? api.userCanBeTracked?.();
    return v ?? false;
  }
  // Fallback localStorage
  const local = readLocal();
  if (local) return local.analytics;
  // Pas de décision encore prise → si zone non régulée → OK, sinon strict (false)
  return !isRegulatedRegion();
}

/**
 * Le user a-t-il accepté le tracking MARKETING (retargeting/ads) ?
 * Concerne : Meta Pixel, Google Ads, Klaviyo marketing, conversion linker.
 */
export function marketingAllowed(): boolean {
  const api = cp();
  if (api) return api.marketingAllowed?.() ?? false;
  const local = readLocal();
  if (local) return local.marketing;
  return !isRegulatedRegion();
}

/**
 * Le user a-t-il accepté le stockage de PRÉFÉRENCES non-essentielles ?
 * Concerne : produits récemment vus et customizations UI passives non
 * strictement nécessaires. La wishlist invitée est un stockage fonctionnel
 * explicitement demandé par l'utilisateur, donc elle n'est pas gated ici.
 *
 * Distinction importante avec analytics : un user peut REFUSER analytics
 * mais ACCEPTER preferences (pour avoir ses last-viewed sans être
 * tracké statistiquement).
 */
export function preferencesAllowed(): boolean {
  const api = cp();
  if (api) {
    // Shopify expose preferencesAllowed() depuis 2024. Si absent → fallback
    // sur userCanBeTracked() (groupé) pour compat anciennes versions API.
    const v = api.preferencesAllowed?.();
    if (typeof v === "boolean") return v;
    return api.userCanBeTracked?.() ?? false;
  }
  const local = readLocal();
  if (local) return local.preferences;
  return !isRegulatedRegion();
}

/**
 * L'user a-t-il déjà répondu à la bannière (any consent given) ?
 * Pratique pour décider si afficher la bannière en fallback.
 */
export function consentGiven(): boolean {
  const api = cp();
  if (api) {
    const v = api.getTrackingConsent?.();
    return v === "yes" || v === "no"; // "" = pas encore décidé
  }
  return readLocal() !== null;
}

function dispatchConsentChanged(): void {
  if (typeof document === "undefined") return;
  document.dispatchEvent(
    new CustomEvent("smootify:consent_changed", {
      detail: {
        analytics: analyticsAllowed(),
        marketing: marketingAllowed(),
        preferences: preferencesAllowed(),
      },
    }),
  );
}

/**
 * Set programmatique du consent. Utilisé par notre fallback banner ou
 * un bouton "Accepter tout" custom dans Webflow.
 *
 * Si Shopify.customerPrivacy est dispo → délègue à l'API Shopify (qui pose
 * un cookie tracking_consent côté Shopify domain). Sinon → écrit dans
 * localStorage `lmdr_consent` (notre fallback).
 */
export function setConsent(
  consent: { analytics?: boolean; marketing?: boolean; preferences?: boolean; sale_of_data?: boolean },
): void {
  const api = cp();
  if (api?.setTrackingConsent) {
    api.setTrackingConsent(consent, dispatchConsentChanged);
    return;
  }
  // Fallback : write localStorage
  writeLocal({
    analytics: !!consent.analytics,
    marketing: !!consent.marketing,
    preferences: !!consent.preferences,
    timestamp: Date.now(),
  });
  dispatchConsentChanged();
}

/**
 * Au boot : écoute `visitorConsentCollected` (event Shopify dispatché
 * quand l'user clique sur la bannière) et bridge vers notre event interne.
 */
export function attachConsentListener(): void {
  if (typeof document === "undefined") return;
  if ((document as unknown as { __lmdrConsentBound?: boolean }).__lmdrConsentBound) return;
  (document as unknown as { __lmdrConsentBound?: boolean }).__lmdrConsentBound = true;

  document.addEventListener("visitorConsentCollected", () => {
    document.dispatchEvent(
      new CustomEvent("smootify:consent_changed", {
        detail: {
          analytics: analyticsAllowed(),
          marketing: marketingAllowed(),
          preferences: preferencesAllowed(),
        },
      }),
    );
  });
}

/**
 * Helper : exécute `fn` immédiatement si consent déjà donné, sinon
 * attend `smootify:consent_changed` et exécute si la condition `check`
 * est remplie. Utile pour init Shopify Analytics, Mixpanel, etc.
 */
export function whenAllowed(
  check: "analytics" | "marketing" | "preferences",
  fn: () => void,
): void {
  const checker = check === "analytics" ? analyticsAllowed
    : check === "marketing" ? marketingAllowed
    : preferencesAllowed;
  if (checker()) {
    fn();
    return;
  }
  if (typeof document === "undefined") return;
  const handler = (): void => {
    if (checker()) {
      document.removeEventListener("smootify:consent_changed", handler);
      fn();
    }
  };
  document.addEventListener("smootify:consent_changed", handler);
}

/**
 * Helper safe storage gated par consent. Utilisé par les modules
 * non-essentiels (wishlist, last-viewed) pour ne RIEN écrire sans consent.
 */
export function safeWriteIfAllowed(
  category: "analytics" | "marketing" | "preferences",
  fn: () => void,
): void {
  const checker = category === "analytics" ? analyticsAllowed
    : category === "marketing" ? marketingAllowed
    : preferencesAllowed;
  if (checker()) fn();
}
